<template>
  <v-container fluid class="elabel-export pa-4 pa-md-8">
    <div class="export-content">
      <header class="d-flex align-center mb-6">
        <v-btn icon class="ml-n3 mr-3" @click="goToProducts"><v-icon color="black">mdi-arrow-left</v-icon></v-btn>
        <div class="page-title mono">{{ $t('elabel_export_title') }}</div>
        <v-spacer />
        <!-- Hidden until the help-center article exists; drop the v-if once the link is set -->
        <v-tooltip v-if="helpCenterLink" bottom content-class="hint-tooltip" :close-delay="2000">
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-bind="attrs" v-on="on" class="help-icon mr-3">mdi-information-outline</v-icon>
          </template>
          <a
            :href="helpCenterLink"
            @click.prevent="openHelpCenterArticle(helpCenterLink)"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ $t('elabel_export_help_link') }}
          </a>
        </v-tooltip>
        <v-btn color="primary" :disabled="loadingCampaigns" data-test="open-export-dialog" @click="dialogOpen = true">
          {{ $t('elabel_export_create') }}
        </v-btn>
      </header>

      <v-card class="history-card" outlined>
        <h2 class="history-title px-4 pt-4 pt-md-6 pb-1">{{ $t('elabel_export_history') }}</h2>
        <p class="history-hint px-4 mb-4" data-test="history-hint">{{ $t('elabel_export_history_hint') }}</p>

        <v-alert v-if="loadError" type="error" text class="mx-4 mx-md-6" data-test="history-error">
          {{ loadError }}
          <v-btn text color="error" class="ml-2" @click="loadHistory()">{{ $t('try_again') }}</v-btn>
        </v-alert>

        <v-data-table
          :headers="headers"
          :items="exports"
          :loading="loading"
          :options.sync="tableOptions"
          :server-items-length="totalCount"
          :items-per-page="PAGE_SIZE"
          :expanded="expandedJobs"
          :no-data-text="$t('elabel_export_not_found')"
          item-key="key"
          disable-sort
          hide-default-footer
          class="history-table"
          data-test="exports-table"
        >
          <template v-slot:[`item.campaigns`]="{ item }">
            <span v-if="!campaignNamesOf(item).length" class="grey--text">—</span>
            <span v-else-if="isAllMarker(campaignNamesOf(item))" class="grey--text" :data-test="'all-campaigns-' + item.key">{{ $t('elabel_export_all_campaigns') }}</span>
            <template v-else>
              {{ leadingCampaignNames(item) }}
              <a
                v-if="hiddenCampaignCount(item)"
                href="#"
                class="more-link"
                :data-test="'more-' + item.key"
                @click.prevent="toggleExpanded(item)"
              >
                {{ $t('plus_count_more', { count: hiddenCampaignCount(item) }) }}
              </a>
              <span v-else-if="isAllMarker(brandNamesOf(item))" class="grey--text ml-1" :data-test="'all-brands-' + item.key">{{ $t('brand_all_brands') }}</span>
              <a
                v-else-if="brandNamesOf(item).length"
                href="#"
                class="more-link"
                :data-test="'brands-' + item.key"
                @click.prevent="toggleExpanded(item)"
              >
                + {{ $tc('elabel_export_brand_count', nameCount(brandNamesOf(item)), { count: nameCount(brandNamesOf(item)) }) }}
              </a>
            </template>
          </template>

          <template v-slot:[`item.products`]="{ item }">
            {{ productCountText(item) }}
          </template>

          <template v-slot:[`item.created`]="{ item }">
            <span :title="formatDateTime(item.created_at)">{{ formatDate(item.created_at) }}</span>
          </template>

          <template v-slot:[`item.status`]="{ item }">
            <div class="status-cell" :data-test="'status-' + item.key">
              <div v-if="canDownload(item)">{{ $t('elabel_export_valid_until', { date: formatDate(item.expires_at) }) }}</div>
              <div v-else-if="isActive(item)" class="d-flex align-center">
                <v-progress-circular indeterminate size="14" width="2" color="primary" class="mr-2" />
                {{ $t('elabel_export_status_generating') }}
              </div>
              <template v-else-if="canRetry(item)">
                <div class="job-error">{{ $t('elabel_export_status_error') }}</div>
                <div v-if="item.error_data && item.error_data.error_message" class="error-note" :title="item.error_data.error_message" :data-test="'error-note-' + item.key">
                  {{ item.error_data.error_message }}
                </div>
              </template>
              <div v-else-if="isExpired(item)" :title="formatDate(item.expires_at)">{{ $t('expired') }}</div>
              <div v-else>{{ item.status }}</div>
            </div>
          </template>

          <template v-slot:[`item.action`]="{ item }">
            <v-btn v-if="canDownload(item)" color="primary" class="status-action" :loading="downloadingKey === item.key" :data-test="'download-' + item.key" @click="download(item.key)">
              {{ $t('download') }}
            </v-btn>
            <v-btn v-else-if="canRetry(item)" outlined color="primary" class="status-action" :loading="retryingKey === item.key" :data-test="'retry-' + item.key" @click="retry(item.key)">
              {{ $t('retry') }}
            </v-btn>
          </template>

          <template v-slot:expanded-item="{ headers: rowHeaders, item }">
            <td :colspan="rowHeaders.length" class="expanded-panel px-4 px-md-6 py-3">
              <div class="caption text-uppercase grey--text text--darken-1 mb-1">
                {{ $t('elabel_export_campaigns_included', { count: nameCount(campaignNamesOf(item)) }) }}
              </div>
              <p class="expanded-names mb-0">{{ namesLine(campaignNamesOf(item)) }}</p>
              <template v-if="brandNamesOf(item).length">
                <div class="caption text-uppercase grey--text text--darken-1 mb-1 mt-3">
                  {{ $t('elabel_export_brands_included', { count: nameCount(brandNamesOf(item)) }) }}
                </div>
                <p class="expanded-names mb-0">{{ namesLine(brandNamesOf(item)) }}</p>
              </template>
            </td>
          </template>

          <template v-slot:footer>
            <div v-show="totalCount > 0" class="mt-5 mb-4">
              <v-pagination
                v-model="tableOptions.page"
                :length="pageCount"
                :total-visible="7"
                color="primary"
                data-test="export-pagination"
              />
            </div>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <elabel-export-dialog
      v-model="dialogOpen"
      :campaigns="campaigns"
      :single-campaign="singleCampaign"
      :submitting="submitting"
      @submit="submit"
    />
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import exportService from '@/services/elabel-export'
import ElabelExportDialog from '@/components/ElabelExportDialog'
import { openHelpCenterArticle } from '@/utils/helpCenter'
import { isExportActive, isExportExpired, canRetryExport, canAccessElabelExport, exportCampaignNames, exportBrandNames, CUT_MARKER, isAllMarker, apiErrorMessage } from '@/utils/elabel-export-state'

const PAGE_SIZE = 10
const LEADING_CAMPAIGN_NAMES = 3

export default {
  name: 'ElabelExport',

  components: { ElabelExportDialog },

  data () {
    return {
      PAGE_SIZE,
      exports: [],
      totalCount: 0,
      tableOptions: { page: 1, itemsPerPage: PAGE_SIZE },
      // Vuetify wants the expanded rows as objects carrying the item key, not the keys themselves
      expandedJobs: [],
      loading: true,
      historyRequested: false,
      submitting: false,
      retryingKey: null,
      downloadingKey: null,
      loadError: null,
      dialogOpen: false,
      pollTimer: null,
      pollStartedAt: null,
      pollFailures: 0
    }
  },

  computed: {
    ...mapGetters(['campaigns', 'loadingCampaigns', 'profile', 'scantrustAuthToken']),

    helpCenterLink () {
      const articles = {
        de: '',
        es: '',
        fr: '',
        it: '',
        pt: '',
        zh: '',
        en: ''
      }
      return articles[this.$i18n.locale] || articles.en
    },

    // Companies with one campaign never see the campaign axis (Products hides it too)
    singleCampaign () {
      return this.campaigns.length === 1
    },

    headers () {
      // The campaigns column takes whatever width the fixed ones leave, so the table
      // fits the card instead of scrolling sideways
      return [
        { text: this.$t('campaigns'), value: 'campaigns' },
        { text: this.$t('elabel_export_col_products'), value: 'products', width: 140, class: 'text-no-wrap' },
        { text: this.$t('created'), value: 'created', width: 120 },
        { text: this.$t('status'), value: 'status', width: 240 },
        { text: '', value: 'action', width: 150 }
      ]
    },

    pageCount () {
      return Math.max(1, Math.ceil(this.totalCount / PAGE_SIZE))
    }
  },

  watch: {
    // The token arrives asynchronously (URL, UAT env or the portal's postMessage); load
    // the history the moment it is there rather than at mount time.
    scantrustAuthToken: {
      immediate: true,
      handler (token) {
        if (token && this.loading && !this.historyRequested) this.loadHistory()
      }
    },

    // The profile may still be loading when this page is opened directly
    profile: {
      immediate: true,
      handler (profile) {
        if (profile && profile.features && !canAccessElabelExport(profile)) {
          this.$router.replace({ name: 'products' })
        }
      }
    },

    'tableOptions.page' () {
      if (!this.historyRequested) return
      // The page body scrolls inside this view's root, not the window
      this.$el.scrollTop = 0
      this.loadHistory()
    }
  },

  beforeDestroy () {
    clearTimeout(this.pollTimer)
  },

  methods: {
    openHelpCenterArticle,
    isActive: isExportActive,
    isExpired: isExportExpired,
    canRetry: canRetryExport,
    campaignNamesOf: exportCampaignNames,
    brandNamesOf: exportBrandNames,
    isAllMarker,

    // A cut list ends with the marker, shown as "(and others)" and left out of counts
    namesLine (names) {
      return names.map(name => name === CUT_MARKER ? this.$t('elabel_export_and_others') : name).join(', ')
    },

    nameCount (names) {
      return names.filter(name => name !== CUT_MARKER).length
    },

    goToProducts () {
      this.$router.push({ name: 'products' })
    },

    showMessage (message) {
      this.$store.dispatch('showSnackBar', { message })
    },

    canDownload (job) {
      return job.status === 'complete' && !this.isExpired(job)
    },

    leadingCampaignNames (job) {
      return this.namesLine(this.campaignNamesOf(job).slice(0, LEADING_CAMPAIGN_NAMES))
    },

    hiddenCampaignCount (job) {
      return Math.max(this.nameCount(this.campaignNamesOf(job)) - LEADING_CAMPAIGN_NAMES, 0)
    },

    toggleExpanded (job) {
      const isExpanded = this.expandedJobs.some(expanded => expanded.key === job.key)
      if (isExpanded) this.expandedJobs = this.expandedJobs.filter(expanded => expanded.key !== job.key)
      else this.expandedJobs = [...this.expandedJobs, { key: job.key }]
    },

    productCountText (job) {
      if (typeof job.elabel_count !== 'number') return '—'
      return new Intl.NumberFormat(this.$i18n.locale).format(job.elabel_count)
    },

    formatDate (value) {
      if (!value) return '—'
      return new Intl.DateTimeFormat(this.$i18n.locale, { dateStyle: 'medium' }).format(new Date(value))
    },

    formatDateTime (value) {
      if (!value) return ''
      return new Intl.DateTimeFormat(this.$i18n.locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
    },

    async loadHistory ({ silent = false } = {}) {
      this.historyRequested = true
      const requestedPage = this.tableOptions.page
      if (!silent) {
        this.loading = true
        this.loadError = null
      }
      const wereActive = this.exports.filter(this.isActive).map(job => job.key)
      let historyPage = null
      let loadFailure = null
      try {
        historyPage = await exportService.loadExports({
          limit: PAGE_SIZE,
          offset: (requestedPage - 1) * PAGE_SIZE
        })
      } catch (error) {
        loadFailure = error
      }
      // The user may have switched to another page while this list was loading. Its result is then
      // for the old page, so it is thrown away instead of replacing the rows now on screen.
      if (requestedPage !== this.tableOptions.page) return
      this.loading = false
      if (loadFailure) {
        if (silent) throw loadFailure
        this.loadError = apiErrorMessage(loadFailure, this.$t('elabel_export_load_failed'))
        return
      }
      this.exports = historyPage.results
      this.totalCount = historyPage.count
      this.pollFailures = 0
      wereActive.forEach(key => {
        const job = historyPage.results.find(item => item.key === key)
        if (job && this.canDownload(job)) {
          this.showMessage(this.$t('elabel_export_ready'))
        }
      })
      if (historyPage.results.some(this.isActive)) this.startPolling()
    },

    // One request refreshes every running export on the current page. Polling runs while
    // anything is active and only ends when the server has settled all of them, so no row
    // goes stale. A new submit restarts the fast schedule.
    startPolling () {
      if (this.pollTimer && this.pollStartedAt) return
      this.pollStartedAt = Date.now()
      this.pollFailures = 0
      this.schedulePoll(2000)
    },

    schedulePoll (delay) {
      clearTimeout(this.pollTimer)
      this.pollTimer = setTimeout(this.poll, delay)
    },

    pollInterval (elapsedMs) {
      if (elapsedMs < 30000) return 2000
      if (elapsedMs < 2 * 60000) return 5000
      return 30000
    },

    async poll () {
      if (!this.exports.some(this.isActive)) {
        this.pollTimer = null
        this.pollStartedAt = null
        return
      }
      if (document.hidden || !navigator.onLine || this.loading) {
        this.schedulePoll(this.loading ? 2000 : 5000)
        return
      }
      try {
        await this.loadHistory({ silent: true })
        if (this.exports.some(this.isActive)) this.schedulePoll(this.pollInterval(Date.now() - this.pollStartedAt))
        else this.pollTimer = null
      } catch (error) {
        this.pollFailures += 1
        this.schedulePoll(Math.min(this.pollFailures * 5000, 30000))
      }
    },

    // A new export always lands at the top of page 1, so show that page
    async showNewJob () {
      this.tableOptions.page = 1
      await this.loadHistory()
      this.pollStartedAt = Date.now()
      if (this.exports.some(this.isActive)) this.schedulePoll(2000)
    },

    async submit (scope) {
      this.submitting = true
      try {
        await exportService.submitExport(scope)
        this.dialogOpen = false
        await this.showNewJob()
      } catch (error) {
        this.showMessage(apiErrorMessage(error, this.$t('elabel_export_submit_failed')))
      } finally {
        this.submitting = false
      }
    },

    async retry (key) {
      this.retryingKey = key
      try {
        await exportService.retryExport(key)
        await this.showNewJob()
      } catch (error) {
        this.showMessage(apiErrorMessage(error, this.$t('elabel_export_retry_failed')))
      } finally {
        this.retryingKey = null
      }
    },

    async download (key) {
      this.downloadingKey = key
      try {
        const link = await exportService.getDownloadLink(key)
        window.location.assign(link)
      } catch (error) {
        this.showMessage(apiErrorMessage(error, this.$t('elabel_export_download_failed')))
      } finally {
        this.downloadingKey = null
      }
    }
  }
}
</script>

<style lang="sass" scoped>
$black: #333333
$dark-grey: #555555
$primary: #15bbf0
$red: #f22a42
$light-bg: #fafafa

.elabel-export
  height: calc(100vh - 63px)
  overflow-y: auto

.export-content
  max-width: 1180px
  margin: 0 auto

.help-icon
  align-self: center
  cursor: pointer

.history-card
  border-color: rgba($black, .12) !important
  border-radius: 4px
  overflow: hidden

.history-hint
  color: $dark-grey
  font-size: 13px

.history-title
  color: $black
  font-size: 20px
  line-height: 1.3

.more-link
  margin-left: 4px
  color: $primary
  text-decoration: none
  white-space: nowrap

.status-cell
  padding: 8px 0
  white-space: nowrap

.status-action
  width: 100%
  min-width: 128px !important

.job-error
  color: $red

.error-note
  color: $dark-grey
  font-size: 12px
  line-height: 1.4
  white-space: normal
  overflow-wrap: anywhere
  display: -webkit-box
  -webkit-line-clamp: 3
  -webkit-box-orient: vertical
  overflow: hidden

.expanded-panel
  background: $light-bg

.history-table ::v-deep .v-data-table__wrapper tbody tr.v-data-table__expanded__content
  box-shadow: none

.expanded-names
  font-size: 13px
  line-height: 1.5
</style>
